import React from "react"
import { useRef, useEffect } from "react"
import DifficultyLevel from "../../components/MultipleChoiceQuestion/components/DifficultyLevel/DifficultyLevel"
import MathKatex from "../../components/MathKatex"
import { IFlashcard } from "./types"
import { restUpdateFlashcardState } from "../../services/flashcards/flashcards-rest-interface"
import { getTimeDiffSinceStartInSeconds } from "../../services/utils/date-util"
import { Context, Resource, UserActivityEx, sendUserActivity } from "../../services/liveview/liveview-service"
import { TIMEOUT_IN_SECONDS } from "../../constants"

interface Props {
    flashcard: IFlashcard
    category: string
    onNextCard: () => void
    flashcardIndexInfo: string
    onClose: () => void
}

interface State {
    showFront: boolean
    startTime: number
}

export default class Flashcard extends React.Component<Props, State> {
    constructor(props) {
        super(props)
        this.state = {
            showFront: true,
            startTime: Date.now()
        }
    }

    onToggleCard = () => {
        this.setState({showFront: !this.state.showFront})
    }

    onMarkAsReview = () => {
        let {onNextCard, flashcard} = this.props
        flashcard.status = "review"
        this.setState({showFront: !this.state.showFront})
        this.updateFlashcardStateInBackend(flashcard)
        onNextCard()
    }

    onMarkAsMastered  = () => {
        let {onNextCard, flashcard} = this.props
        flashcard.status = "mastered"
        this.setState({showFront: !this.state.showFront})
        this.updateFlashcardStateInBackend(flashcard)
        onNextCard()
    }

    updateFlashcardStateInBackend(flashcard: IFlashcard) {
        let studyTimeInSeconds = getTimeDiffSinceStartInSeconds(this.state.startTime)
        this.setState({startTime: Date.now()}) // Reset
        restUpdateFlashcardState(flashcard.id, flashcard.status, studyTimeInSeconds)

        let timedout = studyTimeInSeconds < TIMEOUT_IN_SECONDS ? false: true
        let context = Context.FLASHCARD
        let contextId = "basic"
        let category = flashcard.category
        let resource = Resource.QUESTION
        let resourceId = flashcard.id
        let ended = studyTimeInSeconds
        let userActivityEx: UserActivityEx = {
            context, contextId, 
            resource, resourceId, 
            category,
            timedout,
            ended
        }
        sendUserActivity(userActivityEx, true)
    }

    render() {
        let {flashcard, flashcardIndexInfo, onClose} = this.props
        let {showFront} = this.state
        let {levelOfDifficulty, subcategory} = flashcard
        let title = getFullTitle(flashcard)
        let subtitle = subcategory

        return (
            <div className={`flashcard ${!showFront ? 'flipped' : ''}`}>
                <div className="d-flex flex-column flashcard--header">
                    <div className="d-flex justify-content-between flashcard--modal-header">
                        <h2 className="mb-0">
                            {title}
                            { subtitle && <h5 className="mb-0 pt-1">{subtitle}</h5> }
                        </h2>
                        <button className="closeBtn btn-simple" title="Close modal" onClick={onClose} tabIndex={0}>
                            <img
                                className="modal-icon"
                                src={`/assets/images/icons/v2/ico-modal-close.svg`}
                                width="24"
                                height="24"
                                alt=""
                            />
                        </button>
                    </div>
                    <div className="d-flex justify-content-between flashcard--index">
                        <div className="">
                            {flashcardIndexInfo}
                        </div>
                        <div className="flashcard--index--chart d-flex">
                            <span className="d-inline-block mr-2">Level of Difficulty: </span>
                            <DifficultyLevel difficultyLevel={levelOfDifficulty} popoverXpos={'right'}/>
                        </div>
                    </div>
                </div>
                <div className="flashcard--inner">
                    <div className="flashcard--front">
                        <FlashcardFront flashcard={flashcard} 
                                onToggleCard={this.onToggleCard}
                                onClose={onClose}
                            />
                    </div>
                    <div className="flashcard--back">
                        <FlashcardBack flashcard={flashcard} 
                                onToggleCard={this.onToggleCard}
                                onMarkAsReview={this.onMarkAsReview}
                                onMarkAsMastered={this.onMarkAsMastered}
                                onClose={onClose}
                            />
                    </div>
                </div>
            </div>
        )
    }
}

//--- Helpers ---
/**
 * Button titles map based on the current state
 */
const BTN_TITLES_MAP = {
    "new":      { review: "REVIEW LATER", mastered: "MARK AS MASTERED"},
    "review":   { review: "KEEP FOR REVIEW", mastered: "MARK AS MASTERED"},
    "mastered": { review: "MOVE TO REVIEW", mastered: "KEEP AS MASTERED"},
}

/**
 * Flashcard main title map based on the current state
 */
const CARD_TITLES_MAP = {
    "new":      { title: "New"},
    "review":   { title: "Review Again"},
    "mastered": { title: "Mastered"},
}

const CATEGORY_DIPLAYNAMES_MAP = {
    "English":  { displayName: "English"},
    "Math":     { displayName: "Math"},
    "Science":  { displayName: "Science"},
    "SATVocab": { displayName: "SAT Vocab"},
}

function getButtonTitles(flashcard: IFlashcard) {
    let {status} = flashcard
    return BTN_TITLES_MAP[status]
}

function getFlascardTitle(flashcard: IFlashcard) {
    let {status} = flashcard
    return CARD_TITLES_MAP[status].title
}

function getDisplayName(flashcard: IFlashcard) {
    let {category} = flashcard
    let displayName = CATEGORY_DIPLAYNAMES_MAP[category].displayName
    return displayName
}

function getFullTitle(flashcard: IFlashcard) {
    let title = `${getFlascardTitle(flashcard)}: ${getDisplayName(flashcard)} Flashcards`
    return title
}

interface IFlashcardFront {
    flashcard: IFlashcard,
    onToggleCard: () => void,
    onClose: () => void,
}

interface IFlashcardBack extends IFlashcardFront {
    onMarkAsReview: () => void,
    onMarkAsMastered: () => void,
}

function FlashcardFront({flashcard, onToggleCard, onClose}: IFlashcardFront) {
    let {cardFront} = flashcard

    return (
        <div className="h-100">
            <div className="flashcard--label">QUESTION:</div>
            <div className="flashcard--content d-flex justify-content-between flex-column">
                <div className="flashcard--question">
                    <MathKatex mixedContent={cardFront} classNameForMath="flashcard-math"/>
                    <br/>
                </div>
                <div className="d-flex justify-content-between">
                    <button className="btn btn--bg-blue btn--txt-default" onClick={onToggleCard}>VIEW ANSWER</button>
                </div>
            </div>
        </div>
    )
}

function FlashcardBack({flashcard, onToggleCard, onMarkAsReview, onMarkAsMastered, onClose}: IFlashcardBack) {
    let {cardBack} = flashcard
    let buttonTitles = getButtonTitles(flashcard)
    let questionRef = useRef<HTMLDivElement>(null)

    useEffect(() => {
        if (questionRef.current) {
            questionRef.current.scrollTop = 0
        }
    }, [cardBack])

    return (
        <div className="h-100">
            <div className="flashcard--label">ANSWER:</div>
            <div className="flashcard--content d-flex justify-content-between flex-column">
                <div className="flashcard--question" ref={questionRef}>
                    <MathKatex mixedContent={cardBack} classNameForMath="flashcard-math"/>
                    <br/>
                </div>
                <div className="d-flex flex-wrap">
                    <button className="mr-2 btn btn--bg-blue btn--txt-default" onClick={onToggleCard}>VIEW QUESTION</button>
                    <button className="mr-2 btn btn--bg-green btn--txt-default" onClick={onMarkAsReview}>{buttonTitles.review}</button>
                    <button className="btn btn--bg-purple btn--txt-default" onClick={onMarkAsMastered}>{buttonTitles.mastered}</button>
                </div>
            </div>
        </div>
    )
}
